import { db } from '../lib/firebase';
import { doc, getDoc, deleteDoc, setDoc, updateDoc, increment, writeBatch } from 'firebase/firestore';
import { OnboardingUserRole, DemoAppData } from '../types/onboarding';
import { 
  getAnalystDemoData, 
  getDeveloperDemoData, 
  getViewerDemoData 
} from '../config/onboarding';

export interface DemoAppConfig {
  userId: string;
  role: OnboardingUserRole;
}

export interface DemoEvent {
  id: string;
  title: string;
  description: string;
  type: 'critical' | 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'resolved' | 'rejected';
  coordinates: { x: number; y: number };
  createdBy: string;
  createdAt: Date;
  assignedTo?: string;
  comments?: Array<{
    id: string;
    text: string;
    author: string;
    createdAt: Date;
  }>;
}

// Cloudinary configuration
const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || '';
const CLOUDINARY_UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || '';

/**
 * Upload image to Cloudinary
 * @param file - File to upload
 * @returns Promise with secure_url from Cloudinary
 */
export async function uploadImageToCloudinary(file: File): Promise<string> {
  if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
    throw new Error('Cloudinary configuration missing. Please set VITE_CLOUDINARY_CLOUD_NAME and VITE_CLOUDINARY_UPLOAD_PRESET');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Cloudinary upload failed: ${error.message || 'Unknown error'}`);
  }

  const data = await response.json();
  return data.secure_url;
}

/**
 * Convert local image URL to Cloudinary URL by uploading
 * @param imageUrl - Local image URL (e.g., '/onboard-1.jpg')
 * @returns Promise with Cloudinary secure_url
 */
export async function uploadLocalImageToCloudinary(imageUrl: string): Promise<string> {
  try {
    // Fetch the local image
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${imageUrl}`);
    }

    const blob = await response.blob();
    const filename = imageUrl.split('/').pop() || 'image.jpg';
    const file = new File([blob], filename, { type: blob.type });

    // Upload to Cloudinary
    return await uploadImageToCloudinary(file);
  } catch (error) {
    console.warn(`[demoAppService] Failed to upload ${imageUrl} to Cloudinary, using local URL:`, error);
    return imageUrl;
  }
}

/**
 * Получить демо-данные для указанной роли
 */
function getDemoDataForRole(role: OnboardingUserRole, userId: string): DemoAppData {
  switch (role) {
    case 'analyst':
      return getAnalystDemoData(userId);
    case 'developer':
      return getDeveloperDemoData(userId);
    case 'viewer':
      return getViewerDemoData(userId);
    default:
      // Fallback на analyst если роль неизвестна
      console.warn(`[demoAppService] Unknown role "${role}", falling back to analyst`);
      return getAnalystDemoData(userId);
  }
}

/**
 * Получить суффикс ID для роли
 */
function getRoleSuffix(role: OnboardingUserRole): string {
  switch (role) {
    case 'analyst':
      return 'analyst';
    case 'developer':
      return 'dev';
    case 'viewer':
      return 'viewer';
    default:
      return 'demo';
  }
}

export async function createDemoApp(config: DemoAppConfig): Promise<string> {
  const { userId, role = 'analyst' } = config;
  console.log(`[demoAppService] Starting createDemoApp for user: ${userId}, role: ${role}`);

  const now = new Date();
  const roleSuffix = getRoleSuffix(role);
  const appId = `demo-${roleSuffix}-${userId}`;

  console.log(`[demoAppService] Checking if demo exists for appId: ${appId}`);

  try {
    // Сначала проверяем существует ли демо
    const appRef = doc(db, 'apps', appId);
    const appSnap = await getDoc(appRef);

    if (appSnap.exists()) {
      console.log('[demoAppService] Demo already exists, skipping creation');
      return appId;
    }

    console.log('[demoAppService] Demo does not exist, creating...');

    // Получаем демо-данные для указанной роли
    const demoData = getDemoDataForRole(role, userId);
    console.log(`[demoAppService] Loaded demo data for role "${role}":`, {
      appName: demoData.name,
      versionsCount: demoData.versions.length,
      firstScreenName: demoData.versions[0]?.screens[0]?.name || 'NULL',
      firstScreenImageUrl: demoData.versions[0]?.screens[0]?.imageUrl ? 'EXISTS' : 'NULL'
    });

    // Подготовить версии с правильными датами, eventCount и конвертировать события
    const versions = demoData.versions.map(v => ({
      ...v,
      releaseDate: v.releaseDate || now.toISOString(),
      createdAt: v.createdAt || now.toISOString(),
      // Добавляем eventCount для проверки лимитов (firestore.rules)
      eventCount: v.eventCount ?? v.screens?.reduce((acc, s) => acc + (s.events?.length || 0), 0) ?? 0,
      screens: v.screens.map(s => ({
        ...s,
        createdAt: s.createdAt || now.toISOString(),
        // Конвертировать DemoEvent в AnalyticsEvent формат
        events: (s.events || []).map(e => ({
          id: e.id,
          name: e.title, // Конвертируем title в name
          type: e.type,
          description: e.description,
          x: e.coordinates.x,
          y: e.coordinates.y,
          params: e.params || [],
          comments: (e.comments || []).map(c => ({
            id: c.id,
            author: c.author,
            text: c.text,
            createdAt: c.createdAt
          })),
          status: e.status,
          createdBy: e.createdBy,
          lastStatusChangedBy: e.createdBy,
        }))
      }))
    }));

    // 5. Создать документ приложения со всеми данными
    // Используем роль пользователя для members (analyst, developer, viewer)
    const memberRole = role === 'viewer' ? 'viewer' : role === 'analyst' ? 'analyst' : 'developer';
    // Добавляем status: 'active' для соответствия firestore.rules (hasProjectAccess)
    // Используем DEMO_APP_ICON из demoData
    const appData = {
      id: appId, // Добавляем ID в данные
      name: demoData.name,
      description: demoData.description,
      icon: demoData.icon || '🛍️', // Используем icon из demoData или значение по умолчанию
      ownerId: userId,
      members: { [userId]: { role: memberRole, status: 'active', joinedAt: now.toISOString() } },
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      isDemo: true,
      isOnboarding: true,
      demoForUserId: userId,
      demoRole: role,
      versions: versions
    };

    console.log('[demoAppService] App data prepared:', {
      id: appId,
      name: appData.name,
      ownerId: appData.ownerId,
      demoRole: role,
      versionsCount: versions.length,
      screensCount: versions.reduce((acc, v) => acc + v.screens.length, 0),
      eventsCount: versions.reduce((acc, v) => acc + v.screens.reduce((sacc, s) => sacc + (s.events?.length || 0), 0), 0),
      firstScreenName: versions[0]?.screens[0]?.name || 'NULL',
      firstScreenImageUrl: versions[0]?.screens[0]?.imageUrl ? 'EXISTS' : 'NULL'
    });

    // 6. Записываем данные используя атомарную операцию
    const batch = writeBatch(db);
    
    // Операция А: Создание демо-проекта
    batch.set(appRef, appData);
    
    // Операция Б: Инкремент счётчика demo проектов
    batch.update(doc(db, 'users', userId), { demoProjectCount: increment(1) });
    
    await batch.commit();
    console.log('[demoAppService] Demo app created successfully with atomic operation:', appId);

    return appId;
  } catch (error) {
    console.error('[demoAppService] Failed to create demo app:', error);
    throw error;
  }
}

export async function deleteDemoApp(userId: string, role?: OnboardingUserRole): Promise<void> {
  // Если роль не указана, пробуем удалить все возможные демо
  const roles: OnboardingUserRole[] = role ? [role] : ['analyst', 'developer', 'viewer'];
  
  console.log(`[demoAppService] ========== deleteDemoApp START ==========`);
  console.log(`[demoAppService] userId: ${userId}, roles:`, roles);
  
  let deletedCount = 0;
  let notFoundCount = 0;
  let errorCount = 0;
  
  for (const r of roles) {
    const roleSuffix = getRoleSuffix(r);
    const appId = `demo-${roleSuffix}-${userId}`;
    console.log(`[demoAppService] --- Processing role: ${r}, appId: ${appId} ---`);
    
    try {
      const appRef = doc(db, 'apps', appId);
      console.log(`[demoAppService] Fetching document: apps/${appId}`);
      
      const appSnap = await getDoc(appRef);
      console.log(`[demoAppService] Document exists: ${appSnap.exists()}`);
      
      if (appSnap.exists()) {
        const appData = appSnap.data();
        console.log(`[demoAppService] Document data for ${appId}:`, {
          name: appData.name,
          isDemo: appData.isDemo,
          isDemoType: typeof appData.isDemo,
          ownerId: appData.ownerId,
          ownerIdType: typeof appData.ownerId,
          demoForUserId: appData.demoForUserId,
          demoForUserIdType: typeof appData.demoForUserId,
          demoRole: appData.demoRole,
          userIdTryingToDelete: userId,
          userIdType: typeof userId
        });
        
        // Проверяем isDemo флаг - демо-проекты должны иметь isDemo: true
        // Добавляем строгую проверку на boolean true
        const isDemoFlagValid = appData.isDemo === true;
        console.log(`[demoAppService] isDemo check: ${appData.isDemo} === true ? ${isDemoFlagValid}`);
        
        if (!isDemoFlagValid) {
          console.warn(`[demoAppService] WARNING: Project ${appId} exists but isDemo is NOT true! isDemo=${appData.isDemo}, type=${typeof appData.isDemo}. Skipping deletion.`);
          notFoundCount++;
          continue;
        }
        
        // Проверяем права - владелец должен совпадать
        // Используем строгое сравнение строк
        const ownerIdMatch = String(appData.ownerId || '') === String(userId);
        const demoForUserIdMatch = String(appData.demoForUserId || '') === String(userId);
        const hasPermission = ownerIdMatch || demoForUserIdMatch;
        
        console.log(`[demoAppService] Permission check for ${appId}:`, {
          ownerIdMatch: `${appData.ownerId} === ${userId} ? ${ownerIdMatch}`,
          demoForUserIdMatch: `${appData.demoForUserId} === ${userId} ? ${demoForUserIdMatch}`,
          hasPermission: hasPermission
        });
        
        if (!hasPermission) {
          console.warn(`[demoAppService] WARNING: User ${userId} is not owner of ${appId}! ownerId: ${appData.ownerId}, demoForUserId: ${appData.demoForUserId}`);
          errorCount++;
          continue;
        }
        
        // Используем writeBatch для атомарной операции
        const batch = writeBatch(db);
        
        // Операция А: Удаление демо-проекта
        batch.delete(appRef);
        
        // Операция Б: Декремент счётчика demo проектов
        batch.update(doc(db, 'users', userId), { demoProjectCount: increment(-1) });
        
        await batch.commit();
        console.log(`[demoAppService] Successfully deleted with atomic operation: ${appId}`);
        
        deletedCount++;
      } else {
        console.log(`[demoAppService] Document does not exist: apps/${appId}`);
        notFoundCount++;
      }
    } catch (error) {
      console.error(`[demoAppService] ERROR deleting demo app ${appId}:`, error);
      if (error instanceof Error) {
        console.error(`[demoAppService] Error name: ${error.name}`);
        console.error(`[demoAppService] Error message: ${error.message}`);
        console.error(`[demoAppService] Error stack:`, error.stack);
      }
      errorCount++;
    }
  }
  
  console.log(`[demoAppService] ========== deleteDemoApp SUMMARY ==========`);
  console.log(`[demoAppService] Total roles checked: ${roles.length}`);
  console.log(`[demoAppService] Successfully deleted: ${deletedCount}`);
  console.log(`[demoAppService] Not found: ${notFoundCount}`);
  console.log(`[demoAppService] Errors: ${errorCount}`);
  console.log(`[demoAppService] ========== deleteDemoApp END ==========`);
}

export async function hasDemoApp(userId: string, role?: OnboardingUserRole): Promise<boolean> {
  // Если роль указана, проверяем только для этой роли
  // Иначе проверяем все роли
  const roles: OnboardingUserRole[] = role ? [role] : ['analyst', 'developer', 'viewer'];
  
  console.log(`[demoAppService] hasDemoApp checking for user: ${userId}, roles:`, roles);
  
  for (const r of roles) {
    const roleSuffix = getRoleSuffix(r);
    const appId = `demo-${roleSuffix}-${userId}`;
    
    try {
      const appRef = doc(db, 'apps', appId);
      const appSnap = await getDoc(appRef);
      
      if (appSnap.exists()) {
        const appData = appSnap.data();
        console.log(`[demoAppService] Demo exists for role ${r}:`, {
          appId,
          isDemo: appData.isDemo,
          ownerId: appData.ownerId,
          demoForUserId: appData.demoForUserId,
          name: appData.name
        });
        
        // Проверяем isDemo флаг
        if (appData.isDemo !== true) {
          console.warn(`[demoAppService] WARNING: Project ${appId} exists but isDemo is NOT true! isDemo: ${appData.isDemo}`);
          continue;
        }
        
        return true;
      } else {
        console.log(`[demoAppService] Demo does not exist for role ${r}:`, appId);
      }
    } catch (error) {
      // Если нет доступа к документу, считаем что демо не существует
      console.log(`[demoAppService] Cannot access demo for role ${r}:`, appId, 'Error:', error);
      continue;
    }
  }
  
  console.log('[demoAppService] No demo found for user:', userId);
  return false;
}

/**
 * Получить ID демо-приложения для пользователя и роли
 */
export function getDemoAppId(userId: string, role: OnboardingUserRole): string {
  const roleSuffix = getRoleSuffix(role);
  return `demo-${roleSuffix}-${userId}`;
}

/**
 * Получить существующее демо для пользователя (любой роли)
 */
export async function getExistingDemoAppId(userId: string): Promise<string | null> {
  const roles: OnboardingUserRole[] = ['analyst', 'developer', 'viewer'];
  
  for (const role of roles) {
    const appId = getDemoAppId(userId, role);
    const appRef = doc(db, 'apps', appId);
    const appSnap = await getDoc(appRef);
    
    if (appSnap.exists()) {
      return appId;
    }
  }
  
  return null;
}

/**
 * Проверить, пройден ли тур для демо-проекта в Firebase
 * Возвращает timestamp завершения тура или null если тур не пройден
 */
export async function getTourCompletedAt(userId: string, role: OnboardingUserRole): Promise<string | null> {
  const appId = getDemoAppId(userId, role);
  
  try {
    const appRef = doc(db, 'apps', appId);
    const appSnap = await getDoc(appRef);
    
    if (appSnap.exists()) {
      const appData = appSnap.data();
      // Проверяем isOnboarding - если false, значит тур пройден
      if (appData.isOnboarding === false) {
        console.log(`[demoAppService] Tour completed in Firebase for role ${role}:`, appData.tourCompletedAt);
        return appData.tourCompletedAt || 'completed';
      }
      console.log(`[demoAppService] Tour NOT completed in Firebase for role ${role}: isOnboarding=`, appData.isOnboarding);
      return null;
    }
    
    console.log(`[demoAppService] No demo app found in Firebase for role ${role}`);
    return null;
  } catch (error) {
    console.error(`[demoAppService] Error checking tour status in Firebase:`, error);
    return null;
  }
}

/**
 * Отметить тур как пройденный в Firebase
 */
export async function markTourCompleted(userId: string, role: OnboardingUserRole): Promise<void> {
  const appId = getDemoAppId(userId, role);
  
  try {
    const appRef = doc(db, 'apps', appId);
    const appSnap = await getDoc(appRef);
    
    if (appSnap.exists()) {
      const now = new Date().toISOString();
      await updateDoc(appRef, {
        isOnboarding: false,
        tourCompletedAt: now,
        updatedAt: now
      });
      console.log(`[demoAppService] Tour marked as completed in Firebase for role ${role}`);
    } else {
      console.warn(`[demoAppService] Cannot mark tour completed - demo app not found: ${appId}`);
    }
  } catch (error) {
    console.error(`[demoAppService] Error marking tour completed in Firebase:`, error);
    throw error;
  }
}